import React, { useCallback, useState } from 'react';
import { Clock, Save, RefreshCw, Download, Calculator, Calendar, AlarmClock } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PeriodSection from './PeriodSection';
import WorkScheduleSection from './WorkScheduleSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';
import PaymentSection from './PaymentSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { getDatesBetween, getWorkingDaysBetween } from './helpers/dateHelpers';

// Импорт российских праздников
import { russianHolidaysForYear } from './data/russianHolidays';

/**
 * Компонент основной формы расчета рабочего времени
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  workSchedule,
  setWorkSchedule,
  workScheduleTypes,
  shiftPatterns,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обработка дополнительной логики при изменении определенных полей
      if (name === 'workScheduleType') {
        // Сброс некоторых полей при смене типа графика
        if (value === 'standard') {
          newState.workDaysPerWeek = 5;
          newState.workHoursPerDay = 8;
          newState.shiftPattern = '5/2';
        }
      }

      // Сброс связанных полей если функция отключена
      if (name === 'includeBreaks' && !checked) {
        newState.breakTimeMinutes = 60;
      }
      
      if (name === 'includeHolidays' && !checked) {
        newState.customHolidays = [];
      }
      
      if (name === 'nightShifts' && !checked) {
        newState.nightShiftStartHour = 22;
        newState.nightShiftEndHour = 6;
        newState.nightShiftRate = 1.2;
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      periodType, year, month, startDate, endDate,
      workScheduleType, workDaysPerWeek, workHoursPerDay,
      includeBreaks, breakTimeMinutes,
      nightShifts, nightShiftStartHour, nightShiftEndHour, nightShiftRate,
      overtimeHours, overtimeRate, weekendWork, weekendRate, hourlyRate,
      vacationDays, sickLeaveDays
    } = formData;

    // Валидация периода
    if (periodType === 'custom') {
      if (!startDate) newErrors.startDate = 'Укажите дату начала периода.';
      if (!endDate) newErrors.endDate = 'Укажите дату окончания периода.';
      
      try {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        
        if (isNaN(startDateObj.getTime())) {
          newErrors.startDate = 'Некорректная дата начала.';
        }
        
        if (isNaN(endDateObj.getTime())) {
          newErrors.endDate = 'Некорректная дата окончания.';
        }
        
        if (startDateObj > endDateObj) {
          newErrors.endDate = 'Дата окончания должна быть позже даты начала.';
        }
        
        // Ограничение периода 1 годом
        const diffInDays = Math.round((endDateObj - startDateObj) / (1000 * 60 * 60 * 24));
        if (diffInDays > 366) {
          newErrors.endDate = 'Период не должен превышать 1 год.';
        }
      } catch (e) {
        newErrors.startDate = newErrors.startDate || 'Ошибка проверки дат.';
        newErrors.endDate = newErrors.endDate || 'Ошибка проверки дат.';
      }
    }

    // Валидация графика работы
    if (workScheduleType === 'custom' || workScheduleType === 'standard') {
      const numWorkDaysPerWeek = parseFloatOrDefault(workDaysPerWeek, -1);
      if (numWorkDaysPerWeek <= 0 || numWorkDaysPerWeek > 7) {
        newErrors.workDaysPerWeek = 'Укажите от 1 до 7 рабочих дней в неделю.';
      }
      
      const numWorkHoursPerDay = parseFloatOrDefault(workHoursPerDay, -1);
      if (numWorkHoursPerDay <= 0 || numWorkHoursPerDay > 24) {
        newErrors.workHoursPerDay = 'Укажите от 1 до 24 рабочих часов в день.';
      }
    }

    // Валидация перерывов
    if (includeBreaks) {
      const numBreakTimeMinutes = parseIntOrDefault(breakTimeMinutes, -1);
      if (numBreakTimeMinutes < 0 || numBreakTimeMinutes > 240) {
        newErrors.breakTimeMinutes = 'Время перерыва должно быть от 0 до 240 минут.';
      }
    }

    // Валидация ночных смен
    if (nightShifts) {
      const numNightShiftStartHour = parseIntOrDefault(nightShiftStartHour, -1);
      const numNightShiftEndHour = parseIntOrDefault(nightShiftEndHour, -1);
      const numNightShiftRate = parseFloatOrDefault(nightShiftRate, -1);
      
      if (numNightShiftStartHour < 0 || numNightShiftStartHour > 23) {
        newErrors.nightShiftStartHour = 'Укажите час от 0 до 23.';
      }
      
      if (numNightShiftEndHour < 0 || numNightShiftEndHour > 23) {
        newErrors.nightShiftEndHour = 'Укажите час от 0 до 23.';
      }
      
      if (numNightShiftRate <= 0 || numNightShiftRate > 5) {
        newErrors.nightShiftRate = 'Коэффициент должен быть от 1 до 5.';
      }
    }

    // Валидация сверхурочных
    const numOvertimeHours = parseFloatOrDefault(overtimeHours, -1);
    if (numOvertimeHours < 0 || numOvertimeHours > 500) {
      newErrors.overtimeHours = 'Значение должно быть от 0 до 500 часов.';
    }
    
    const numOvertimeRate = parseFloatOrDefault(overtimeRate, -1);
    if (numOvertimeRate <= 0 || numOvertimeRate > 5) {
      newErrors.overtimeRate = 'Коэффициент должен быть от 1 до 5.';
    }

    // Валидация работы в выходные
    const numWeekendWork = parseFloatOrDefault(weekendWork, -1);
    if (numWeekendWork < 0 || numWeekendWork > 100) {
      newErrors.weekendWork = 'Значение должно быть от 0 до 100 дней.';
    }
    
    const numWeekendRate = parseFloatOrDefault(weekendRate, -1);
    if (numWeekendRate <= 0 || numWeekendRate > 5) {
      newErrors.weekendRate = 'Коэффициент должен быть от 1 до 5.';
    }

    // Валидация ставки
    const numHourlyRate = parseFloatOrDefault(hourlyRate, -1);
    if (numHourlyRate < 0 || numHourlyRate > 10000) {
      newErrors.hourlyRate = 'Ставка должна быть от 0 до 10000 рублей.';
    }

    // Валидация отпуска и больничных
    const numVacationDays = parseIntOrDefault(vacationDays, -1);
    if (numVacationDays < 0 || numVacationDays > 100) {
      newErrors.vacationDays = 'Значение должно быть от 0 до 100 дней.';
    }
    
    const numSickLeaveDays = parseIntOrDefault(sickLeaveDays, -1);
    if (numSickLeaveDays < 0 || numSickLeaveDays > 100) {
      newErrors.sickLeaveDays = 'Значение должно быть от 0 до 100 дней.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета рабочего времени
  const calculateWorkHours = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setWorkSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          periodType, year, month, startDate, endDate,
          workScheduleType, workDaysPerWeek, workHoursPerDay,
          includeBreaks, breakTimeMinutes,
          includeHolidays, nightShifts, nightShiftStartHour, nightShiftEndHour, nightShiftRate,
          overtimeHours, overtimeRate, weekendWork, weekendRate, hourlyRate,
          vacationDays, sickLeaveDays, shiftPattern, useProductionCalendar
        } = formData;
        
        // Определяем период расчета
        let periodStartDate, periodEndDate;
        let periodName = '';
        
        switch (periodType) {
          case 'month':
            // Для месяца
            const selectedMonth = new Date(parseInt(year), parseInt(month), 1);
            const nextMonthStart = new Date(parseInt(year), parseInt(month) + 1, 1);
            const lastDayOfMonth = new Date(nextMonthStart - 1);
            
            periodStartDate = selectedMonth;
            periodEndDate = lastDayOfMonth;
            
            const monthNames = [
              'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
              'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
            ];
            periodName = `${monthNames[parseInt(month)]} ${year}`;
            break;
          
          case 'year':
            // Для года
            periodStartDate = new Date(parseInt(year), 0, 1);
            periodEndDate = new Date(parseInt(year), 11, 31);
            periodName = `${year} год`;
            break;
          
          case 'custom':
            // Для произвольного периода
            periodStartDate = new Date(startDate);
            periodEndDate = new Date(endDate);
            periodName = `${periodStartDate.toLocaleDateString('ru-RU')} - ${periodEndDate.toLocaleDateString('ru-RU')}`;
            break;
            
          default:
            throw new Error('Неизвестный тип периода');
        }
        
        // Получение всех праздников для выбранного периода
        let holidays = [];
        if (includeHolidays && useProductionCalendar) {
          // Получаем праздники для каждого года в периоде
          const startYear = periodStartDate.getFullYear();
          const endYear = periodEndDate.getFullYear();
          
          for (let y = startYear; y <= endYear; y++) {
            holidays = [...holidays, ...russianHolidaysForYear(y)];
          }
        }
        
        // Массив для хранения всех дат периода с их статусом
        const allDatesInPeriod = getDatesBetween(periodStartDate, periodEndDate);
        
        // Определение рабочих и выходных дней
        let workingDays;
        let schedule = [];
        let totalWorkingDays = 0;
        let totalWorkingHours = 0;
        let totalWorkingHoursWithOvertime = 0;
        let totalNightHours = 0;
        let totalWeekendDays = 0;
        let totalHolidays = 0;
        let totalShortenedDays = 0;
        
        // Расчет в зависимости от типа графика
        if (workScheduleType === 'standard' || workScheduleType === 'custom') {
          // Стандартный или произвольный график 
          const effectiveWorkDaysPerWeek = parseFloat(workDaysPerWeek);
          const effectiveWorkHoursPerDay = parseFloat(workHoursPerDay);
          const effectiveBreakTimeHours = includeBreaks ? parseFloat(breakTimeMinutes) / 60 : 0;
          
          // Определяем рабочие дни (без учета праздников)
          workingDays = getWorkingDaysBetween(periodStartDate, periodEndDate, holidays);
          
          // Для стандартного графика (5/2) считаем рабочими пн-пт
          const isWorkingDayInStandardSchedule = (date) => {
            const dayOfWeek = date.getDay(); // 0 = вс, 1 = пн, ..., 6 = сб
            if (workScheduleType === 'standard') {
              return dayOfWeek >= 1 && dayOfWeek <= 5; // Пн-Пт
            } else if (workScheduleType === 'custom') {
              // Для произвольного графика - по указанному количеству рабочих дней
              // Например, если 4 рабочих дня в неделю, считаем Пн-Чт рабочими
              return dayOfWeek >= 1 && dayOfWeek <= effectiveWorkDaysPerWeek;
            }
            return false;
          };
          
          // Проходим по всем датам и формируем график
          schedule = allDatesInPeriod.map(date => {
            const dayOfWeek = date.getDay();
            const dateStr = date.toISOString().split('T')[0];
            
            // Проверяем, является ли день праздником
            const holidayInfo = holidays.find(h => {
              const holidayDate = new Date(h.date);
              return holidayDate.getDate() === date.getDate() && 
                    holidayDate.getMonth() === date.getMonth() &&
                    holidayDate.getFullYear() === date.getFullYear();
            });
            
            // Определяем статус дня
            let dayStatus = 'workday'; // По умолчанию рабочий день
            let workHours = effectiveWorkHoursPerDay;
            let nightHours = 0;
            
            if (holidayInfo) {
              if (holidayInfo.type === 'holiday') {
                dayStatus = 'holiday';
                workHours = 0;
                totalHolidays++;
              } else if (holidayInfo.type === 'shortened') {
                dayStatus = 'shortened';
                workHours = effectiveWorkHoursPerDay - 1; // На час меньше в предпраздничные дни
                totalShortenedDays++;
              }
            } else if (!isWorkingDayInStandardSchedule(date)) {
              dayStatus = 'weekend';
              workHours = 0;
            }
            
            // Если рабочий день
            if (dayStatus === 'workday' || dayStatus === 'shortened') {
              totalWorkingDays++;
              totalWorkingHours += workHours;
              
              // Расчет ночных часов, если включено
              if (nightShifts) {
                // Упрощенный расчет. В реальности нужно учитывать точное время смены
                const nightStart = parseInt(nightShiftStartHour);
                const nightEnd = parseInt(nightShiftEndHour);
                
                // Если ночная смена переходит на следующий день (например, 22:00 - 6:00)
                if (nightStart > nightEnd) {
                  nightHours = Math.min(workHours, 24 - nightStart + nightEnd);
                } else {
                  nightHours = Math.min(workHours, nightEnd - nightStart);
                }
                
                totalNightHours += nightHours;
              }
            } else if (dayStatus === 'weekend') {
              totalWeekendDays++;
            }
            
            return {
              date,
              dayOfWeek,
              dayStatus,
              workHours,
              nightHours,
              holidayInfo: holidayInfo ? holidayInfo.name : null
            };
          });
          
        } else if (workScheduleType === 'shift') {
          // Сменный график
          const effectiveWorkHoursPerDay = parseFloat(workHoursPerDay);
          const effectiveBreakTimeHours = includeBreaks ? parseFloat(breakTimeMinutes) / 60 : 0;
          
          // Определяем шаблон смены (например, 2/2 - 2 дня работы, 2 выходных)
          let workDays = 5, restDays = 2; // По умолчанию 5/2
          
          switch (shiftPattern) {
            case '2/2':
              workDays = 2;
              restDays = 2;
              break;
            case '1/3':
              workDays = 1;
              restDays = 3;
              break;
            case '3/1':
              workDays = 3;
              restDays = 1;
              break;
            case '4/3':
              workDays = 4;
              restDays = 3;
              break;
            case '7/7':
              workDays = 7;
              restDays = 7;
              break;
            case '15/15':
              workDays = 15;
              restDays = 15;
              break;
            case '5/2':
            default:
              workDays = 5;
              restDays = 2;
              break;
          }
          
          // Рассчитываем циклы сменности
          const cycleDuration = workDays + restDays;
          let currentDayInCycle = 0; // Начинаем с первого дня цикла
          
          // Проходим по всем датам и формируем график
          schedule = allDatesInPeriod.map(date => {
            const dayOfWeek = date.getDay();
            const dateStr = date.toISOString().split('T')[0];
            
            // Проверяем, является ли день праздником
            const holidayInfo = holidays.find(h => {
              const holidayDate = new Date(h.date);
              return holidayDate.getDate() === date.getDate() && 
                    holidayDate.getMonth() === date.getMonth() &&
                    holidayDate.getFullYear() === date.getFullYear();
            });
            
            // Определяем статус дня по циклу сменности
            let dayStatus = currentDayInCycle < workDays ? 'workday' : 'weekend';
            let workHours = dayStatus === 'workday' ? effectiveWorkHoursPerDay : 0;
            let nightHours = 0;
            
            // Учитываем праздники
            if (holidayInfo && includeHolidays) {
              if (holidayInfo.type === 'holiday' && dayStatus === 'workday') {
                // В праздничные дни по графику - двойная оплата
                dayStatus = 'holiday-workday';
                totalHolidays++;
              } else if (holidayInfo.type === 'shortened' && dayStatus === 'workday') {
                dayStatus = 'shortened';
                workHours = effectiveWorkHoursPerDay - 1; // На час меньше в предпраздничные дни
                totalShortenedDays++;
              } else if (holidayInfo.type === 'holiday') {
                totalHolidays++;
              }
            }
            
            // Если рабочий день
            if (dayStatus === 'workday' || dayStatus === 'shortened' || dayStatus === 'holiday-workday') {
              totalWorkingDays++;
              totalWorkingHours += workHours;
              
              // Расчет ночных часов, если включено
              if (nightShifts) {
                // Упрощенный расчет ночных часов
                const nightStart = parseInt(nightShiftStartHour);
                const nightEnd = parseInt(nightShiftEndHour);
                
                if (nightStart > nightEnd) {
                  nightHours = Math.min(workHours, 24 - nightStart + nightEnd);
                } else {
                  nightHours = Math.min(workHours, nightEnd - nightStart);
                }
                
                totalNightHours += nightHours;
              }
            } else if (dayStatus === 'weekend') {
              totalWeekendDays++;
            }
            
            // Переходим к следующему дню цикла
            currentDayInCycle = (currentDayInCycle + 1) % cycleDuration;
            
            return {
              date,
              dayOfWeek,
              dayStatus,
              workHours,
              nightHours,
              holidayInfo: holidayInfo ? holidayInfo.name : null
            };
          });
        }
        
        // Добавляем сверхурочные часы
        const numOvertimeHours = parseFloat(overtimeHours) || 0;
        totalWorkingHoursWithOvertime = totalWorkingHours + numOvertimeHours;
        
        // Добавляем работу в выходные
        const numWeekendWorkDays = parseFloat(weekendWork) || 0;
        const weekendWorkHours = numWeekendWorkDays * parseFloat(workHoursPerDay);
        totalWorkingHoursWithOvertime += weekendWorkHours;
        
        // Расчет оплаты
        const numHourlyRate = parseFloat(hourlyRate) || 0;
        const numVacationDays = parseInt(vacationDays) || 0;
        const numSickLeaveDays = parseInt(sickLeaveDays) || 0;
        
        const baseWageAmount = totalWorkingHours * numHourlyRate;
        const overtimeWageAmount = numOvertimeHours * numHourlyRate * parseFloat(overtimeRate);
        const weekendWageAmount = weekendWorkHours * numHourlyRate * parseFloat(weekendRate);
        const nightWageAmount = totalNightHours * numHourlyRate * parseFloat(nightShiftRate);
        
        // Упрощенный расчет оплаты отпуска и больничных
        const workdaysInYear = 247; // Среднее количество рабочих дней в году
        const avgDailyWage = (baseWageAmount / totalWorkingDays) || 0;
        const vacationWageAmount = avgDailyWage * numVacationDays;
        const sickLeaveWageAmount = avgDailyWage * numSickLeaveDays * 0.8; // 80% от средней ЗП
        
        const totalWageAmount = baseWageAmount + overtimeWageAmount + weekendWageAmount + 
                               nightWageAmount + vacationWageAmount + sickLeaveWageAmount;
        
        // --- Готовим итоговые результаты ---
        const finalResults = {
          periodName,
          periodStart: periodStartDate,
          periodEnd: periodEndDate,
          
          totalDays: allDatesInPeriod.length,
          totalWorkingDays,
          totalWeekendDays,
          totalHolidays,
          totalShortenedDays,
          
          totalWorkingHours,
          totalWorkingHoursWithOvertime,
          totalNightHours,
          overtimeHours: numOvertimeHours,
          weekendWorkHours,
          
          hourlyRate: numHourlyRate,
          baseWageAmount,
          overtimeWageAmount,
          weekendWageAmount,
          nightWageAmount,
          vacationWageAmount,
          sickLeaveWageAmount,
          totalWageAmount,
          
          vacationDays: numVacationDays,
          sickLeaveDays: numSickLeaveDays,
          
          workScheduleType,
          shiftPattern: workScheduleType === 'shift' ? shiftPattern : null
        };
        
        setResults(finalResults);
        setWorkSchedule(schedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setWorkSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setWorkSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      periodType: 'month',
      year: new Date().getFullYear(),
      month: new Date().getMonth(),
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
      workScheduleType: 'standard',
      workDaysPerWeek: 5,
      workHoursPerDay: 8,
      includeBreaks: true,
      breakTimeMinutes: 60,
      includeHolidays: true,
      customHolidays: [],
      nightShifts: false,
      nightShiftStartHour: 22,
      nightShiftEndHour: 6,
      nightShiftRate: 1.2,
      overtimeHours: 0,
      overtimeRate: 1.5,
      weekendWork: 0,
      weekendRate: 2.0,
      hourlyRate: 250,
      vacationDays: 0,
      sickLeaveDays: 0,
      shiftPattern: '5/2',
      useProductionCalendar: true,
    });
    setResults(null);
    setWorkSchedule([]);
    setErrors({});
    setSaveName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика работы в CSV
  const exportToCSV = () => {
    if (!workSchedule || workSchedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = [
      'Дата', 
      'День недели', 
      'Статус', 
      'Рабочие часы',
      'Ночные часы', 
      'Примечание'
    ];

    // Названия дней недели
    const weekdayNames = [
      'Воскресенье', 'Понедельник', 'Вторник', 'Среда', 
      'Четверг', 'Пятница', 'Суббота'
    ];

    // Статусы дней
    const statusNames = {
      'workday': 'Рабочий день',
      'weekend': 'Выходной',
      'holiday': 'Праздник',
      'shortened': 'Сокращенный день',
      'holiday-workday': 'Праздничный рабочий день'
    };

    const rows = workSchedule.map(item => {
      const dateStr = item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const weekdayName = weekdayNames[item.dayOfWeek];
      const statusName = statusNames[item.dayStatus] || item.dayStatus;
      const workHours = item.workHours > 0 ? item.workHours.toString() : '-';
      const nightHours = item.nightHours > 0 ? item.nightHours.toString() : '-';
      const notes = item.holidayInfo || '';
      
      return [dateStr, weekdayName, statusName, workHours, nightHours, notes].join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 
      'ИТОГО:', 
      `${results.totalWorkingDays} рабочих дней`,
      `${results.totalWorkingHours} ч.`,
      `${results.totalNightHours} ч.`,
      ''
    ].join(';');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Период:', results.periodName],
      ['Тип графика:', workScheduleTypes.find(t => t.id === results.workScheduleType)?.name || results.workScheduleType],
      ['Всего дней в периоде:', results.totalDays],
      ['Рабочих дней:', results.totalWorkingDays],
      ['Выходных дней:', results.totalWeekendDays],
      ['Праздничных дней:', results.totalHolidays],
      ['Сокращенных дней:', results.totalShortenedDays],
      ['Рабочих часов:', results.totalWorkingHours],
      ['С учетом сверхурочных:', results.totalWorkingHoursWithOvertime],
      ['Ночных часов:', results.totalNightHours],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'график_работы.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateWorkHours();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Clock className="w-6 h-6 text-cyan-500" />
          Параметры расчета
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры периода */}
        <PeriodSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* График работы */}
        <WorkScheduleSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          workScheduleTypes={workScheduleTypes}
          shiftPatterns={shiftPatterns}
        />

        {/* Дополнительные опции */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Оплата труда */}
        <PaymentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать время'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {workSchedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;